/**
 * codeShield — best-effort frontend deterrents against casual code inspection.
 *
 * IMPORTANT — this is a DETERRENT, not a security boundary. Anyone determined
 * (uses Burp Suite / Postman, disables JavaScript, or simply reads the JS
 * bundle source) can bypass these checks. Real security MUST live on the
 * backend (see /app/backend/security_mw.py for rate limiting + lockouts).
 *
 * What this module does (when REACT_APP_SHIELD_ENABLED=true):
 *  1. Disables right-click context menu
 *  2. Blocks F12 / Ctrl+Shift+I/J/C / Cmd+Option+I/J / Ctrl+U
 *  3. Detects DevTools open via window-dimension diff + debugger-throttle
 *     trick → blanks the page until DevTools is closed
 *  4. Prints a console banner warning ("Stop! Pasting code here may compromise
 *     your account…")
 *  5. Continuously calls console.clear() so logs don't pile up
 *
 * Auto-bypass rules (so we don't lock OUT our own admins):
 *  - Skipped on /root/admin/* and /agent/* paths (admin needs DevTools)
 *  - Skipped if localStorage.shield_off === "1" (manual override)
 *  - Disabled entirely when env REACT_APP_SHIELD_ENABLED !== "true"
 */

const ENABLED = String(process.env.REACT_APP_SHIELD_ENABLED || "").toLowerCase() === "true";

const ADMIN_PATHS = ["/root/admin", "/agent"];

let _devtoolsOverlay = null;
let _intervals = [];

function isAdminPath() {
  const p = window.location.pathname || "";
  return ADMIN_PATHS.some((a) => p === a || p.startsWith(a + "/") || p.startsWith(a));
}

function manualOverride() {
  try {
    return window.localStorage.getItem("shield_off") === "1";
  } catch {
    return false;
  }
}

function showDevtoolsOverlay() {
  if (_devtoolsOverlay) return;
  const el = document.createElement("div");
  el.id = "__shield_overlay";
  el.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "background:#000",
    "color:#fff",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "flex-direction:column",
    "font-family:system-ui,-apple-system,sans-serif",
    "padding:24px",
    "text-align:center",
  ].join(";");
  el.innerHTML = `
    <div style="font-size:48px;margin-bottom:16px">🔒</div>
    <div style="font-size:22px;font-weight:700;margin-bottom:8px">Access denied</div>
    <div style="font-size:14px;opacity:0.7;max-width:480px;line-height:1.6">
      Developer tools are not allowed on this page. Please close DevTools and reload.
    </div>
  `;
  document.body.appendChild(el);
  _devtoolsOverlay = el;
}

function hideDevtoolsOverlay() {
  if (_devtoolsOverlay && _devtoolsOverlay.parentNode) {
    _devtoolsOverlay.parentNode.removeChild(_devtoolsOverlay);
  }
  _devtoolsOverlay = null;
}

function detectDevToolsByDimension() {
  // When devtools is docked on side/bottom, outerWidth-innerWidth > ~160 / outerHeight-innerHeight > ~160
  const widthDiff = Math.abs((window.outerWidth || 0) - (window.innerWidth || 0));
  const heightDiff = Math.abs((window.outerHeight || 0) - (window.innerHeight || 0));
  return widthDiff > 200 || heightDiff > 200;
}

function detectDevToolsByDebugger() {
  // When DevTools is open, `debugger` statement pauses execution noticeably.
  // We measure how long a debugger statement takes to step over.
  const start = performance.now();
  // eslint-disable-next-line no-debugger
  // We avoid actually triggering debugger to not annoy users in normal browsing
  // Just check timing of empty-function call which is a softer hint
  for (let i = 0; i < 1; i++) { /* noop */ }
  return performance.now() - start > 100;
}

function tickDevtoolsCheck() {
  const open = detectDevToolsByDimension() || detectDevToolsByDebugger();
  if (open) showDevtoolsOverlay();
  else hideDevtoolsOverlay();
}

function blockKey(e) {
  const k = (e.key || "").toLowerCase();
  // F12
  if (e.key === "F12") { e.preventDefault(); return false; }
  // Ctrl+U (view source)
  if ((e.ctrlKey || e.metaKey) && k === "u") { e.preventDefault(); return false; }
  // Ctrl+Shift+I/J/C (DevTools)
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && (k === "i" || k === "j" || k === "c")) {
    e.preventDefault();
    return false;
  }
  // Ctrl+S (save page)
  if ((e.ctrlKey || e.metaKey) && k === "s") { e.preventDefault(); return false; }
  return true;
}

function printConsoleBanner() {
  try {
    const big = "color:#dc2626;font-size:42px;font-weight:900;text-shadow:1px 1px 0 #000;";
    const warn = "color:#f59e0b;font-size:16px;font-weight:600;";
    const small = "color:#6b7280;font-size:12px;";
    /* eslint-disable no-console */
    console.log("%cStop!", big);
    console.log("%cThis is a developer console.", warn);
    console.log("%cPasting any code or text here may give attackers access to your account, balance, or personal data. If you don't know what you're doing, close this window.", small);
    console.log("%c© Maxlive88 — code is proprietary and protected.", small);
    /* eslint-enable no-console */
  } catch {}
}

export function installCodeShield() {
  if (!ENABLED) return;
  if (isAdminPath()) return;
  if (manualOverride()) return;

  // 1. Right-click
  document.addEventListener("contextmenu", (e) => { e.preventDefault(); return false; });

  // 2. Keyboard shortcuts
  document.addEventListener("keydown", blockKey, { capture: true });

  // 3. Devtools detector poll (every 1.5s)
  const idDt = setInterval(tickDevtoolsCheck, 1500);
  _intervals.push(idDt);

  // 4. Console banner + clear loop (every 2s)
  printConsoleBanner();
  const idClr = setInterval(() => {
    try {
      // eslint-disable-next-line no-console
      console.clear();
      printConsoleBanner();
    } catch {}
  }, 2500);
  _intervals.push(idClr);
}

export function uninstallCodeShield() {
  _intervals.forEach((id) => clearInterval(id));
  _intervals = [];
  hideDevtoolsOverlay();
}
